import { isNotNullish, isNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { Validator } from '@adornis/validation/validation.js';
import type { ValidationFunction } from '@adornis/validation/validator.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { isLehrkraft } from '@martinus/taetigkeit/enums/Taetigkeiten.js';
import { GermanInsuranceTypes } from '../../../../db/enums/misc-enums.js';
import { Role } from '../../../../permissions/roles.js';
import type { Person } from '../../../../users/entities/person.js';
import { calculateWorkingHoursForEingruppierung } from '../../../api/calculateWorkingHours.js';
import { changeEndDateError, changeStartDateError } from '../../../api/contractTimeframes.js';
import { isPercentSumValid, kostenstellenSumErrorMessage } from '../../../api/kostenstellenPercent.js';
import type { AppliedContract } from '../../../entities/AppliedContract.js';
import { Lohnart } from '../../../enums/Lohnart.js';
import { isTypeWithTariff } from '../../../enums/Entgeltart.js';

type PersonOfContract = Maybe<
  Pick<Person, '_id'> & {
    mitarbeiterDaten?: Maybe<{ anSozialVersicherung?: Maybe<{ versicherungsStatus?: Maybe<string> }> }>;
  }
>;

/**
 * PSB with a global context and admins maintain contracts after the fact (corrections, backdated entries),
 * so the day restrictions of the simple mode do not apply to them.
 */
export const canEditContractDatesFreely = (user: Maybe<Pick<Person, 'roles'>>): boolean =>
  !!user?.roles?.some(r => (r.name === Role.PSB && r.contexts.includes(GLOBAL_CONTEXT)) || r.name === Role.ADMIN);

export interface ContractDateRules {
  /**
   * The dates the contract was loaded with. Only dates the user actually enters are held to the rules of the
   * simple mode - contracts maintained by the Personalabteilung legitimately hold days the simple mode forbids,
   * and those must not block a Schulleitung from saving an unrelated change.
   */
  loaded: { einstellungsdatum: Maybe<Date>; gekuendigtZum: Maybe<Date> };
  /** see {@link canEditContractDatesFreely} */
  canEditDatesFreely: boolean;
}

const isUnchanged = (value: Maybe<Date>, loaded: Maybe<Date>) =>
  (value?.getTime() ?? null) === (loaded?.getTime() ?? null);

/**
 * The Einstellungsdatum is always required. In the simple mode a newly entered start additionally has to be
 * the 1st or the 15th of a month and may not lie in the past - the Personalabteilung enters the real days of a
 * contract in the expert view and is not held to those.
 */
const einstellungsdatumValidator =
  (dateRules: ContractDateRules, simpleValidation: boolean): ValidationFunction<Maybe<Date>, AppliedContract> =>
  options => {
    if (isNullish(options.value)) return nonOptional()(options);

    if (!simpleValidation || dateRules.canEditDatesFreely) return;
    if (isUnchanged(options.value, dateRules.loaded.einstellungsdatum)) return;

    const error = changeStartDateError(options.value);
    if (error) throw new ValidationError(error);
  };

/**
 * An end of the contract is optional (an empty value is an open end). In the simple mode a newly entered end
 * has to be the 14th or the last day of a month, may not lie in the past and may not precede the start.
 */
const gekuendigtZumValidator =
  (dateRules: ContractDateRules, simpleValidation: boolean): ValidationFunction<Maybe<Date>, AppliedContract> =>
  options => {
    if (isNullish(options.value)) return;

    if (!simpleValidation || dateRules.canEditDatesFreely) return;
    if (isUnchanged(options.value, dateRules.loaded.gekuendigtZum)) return;

    const error = changeEndDateError(options.value, (options.source as AppliedContract)?.agInfos?.einstellungsdatum);
    if (error) throw new ValidationError(error);
  };

/**
 * Functional form validator for the contract forms. Mirrors the old entity prototype validators.
 * @param getPerson the person the contract belongs to. Needed for social security checks.
 * @param simpleValidation the simple mode (Schulleitung) validates less fields, as those get filled by the Personalabteilung later.
 * @param dateRules the state the date rules of the simple mode are checked against.
 */
export const buildContractValidator = (
  getPerson: () => PersonOfContract,
  simpleValidation: boolean,
  dateRules: ContractDateRules,
): Validator => {
  const skipInSimpleMode = (validator: (options: any) => void) => (options: any) => {
    if (simpleValidation) return;
    return validator(options);
  };

  const beitragsgruppenschluessel = skipInSimpleMode(options => nonOptional({ allowFalsy: true })(options));

  const eingruppierungValidator = (path: 'agEingruppierung1' | 'agEingruppierung2') => {
    const sectionExists = (options: any) => isNotNullish((options.source as AppliedContract)?.[path]);
    const onlyIfSectionExists = (validator: (options: any) => void) => (options: any) => {
      if (!sectionExists(options)) return;
      return validator(options);
    };

    const isLehrkraftSection = (options: any) =>
      isLehrkraft((options.source as AppliedContract)?.[path]?.agVertragsInfos?.taetigkeit);

    return {
      agVertragsInfos: {
        taetigkeit: onlyIfSectionExists(nonOptional()),
        kostenstelleProzentsaetze: {
          kostenstelleID: onlyIfSectionExists(nonOptional()),
          prozentsatz: onlyIfSectionExists(nonOptional()),
        },
      },
      agArbeitszeit: {
        stunden: onlyIfSectionExists(options => {
          if (!isLehrkraftSection(options)) return nonOptional()(options);
        }),
        maxDeputat: onlyIfSectionExists(options => {
          if (isLehrkraftSection(options)) return nonOptional()(options);
        }),
      },
      agLohnGehaltszahlungen: {
        entgeltart: onlyIfSectionExists(skipInSimpleMode(options => nonOptional()(options))),
        tarifgruppe: onlyIfSectionExists(options => {
          const entgeltart = (options.source as AppliedContract)?.[path]?.agLohnGehaltszahlungen?.entgeltart;
          if (entgeltart && isTypeWithTariff(entgeltart)) return nonOptional()(options);
        }),
        monatlicheZahlungen: {
          lohnart: onlyIfSectionExists(nonOptional()),
          betrag: onlyIfSectionExists(nonOptional()),
        },
      },
    };
  };

  return {
    // stand-in path for contract wide checks, like the old Contract prototype validator did
    createdAt: options => {
      const contract = options.source as AppliedContract;

      const entgeltZahlungenAg1 =
        contract.agEingruppierung1?.agLohnGehaltszahlungen?.monatlicheZahlungen?.filter(
          z => z.lohnart === Lohnart.ENTGELT,
        ) ?? [];
      const entgeltZahlungenAg2 =
        contract.agEingruppierung2?.agLohnGehaltszahlungen?.monatlicheZahlungen?.filter(
          z => z.lohnart === Lohnart.ENTGELT,
        ) ?? [];
      if (entgeltZahlungenAg1.length > 1 || entgeltZahlungenAg2.length > 1)
        throw new ValidationError('Kann maximal eine Zahlung mit Lohnart Entgelt konfigurieren');

      const ag1Kostenstellen = (
        contract.agEingruppierung1?.agVertragsInfos?.kostenstelleProzentsaetze?.map(k => k.prozentsatz) ?? []
      ).filter(isNotNullish);
      const ag2Kostenstellen = (
        contract.agEingruppierung2?.agVertragsInfos?.kostenstelleProzentsaetze?.map(k => k.prozentsatz) ?? []
      ).filter(isNotNullish);

      // validate only ag1
      if (contract.agEingruppierung1) {
        const sumPercentAg1 = ag1Kostenstellen.reduce((a, b) => a + b, 0);
        if (!isPercentSumValid(sumPercentAg1))
          throw new ValidationError(
            kostenstellenSumErrorMessage(
              'AG1',
              sumPercentAg1,
              calculateWorkingHoursForEingruppierung(contract.agEingruppierung1),
            ),
          );
      }

      // validate only ag2
      if (contract.agEingruppierung2) {
        const sumPercentAg2 = ag2Kostenstellen.reduce((a, b) => a + b, 0);
        if (!isPercentSumValid(sumPercentAg2))
          throw new ValidationError(
            kostenstellenSumErrorMessage(
              'AG2',
              sumPercentAg2,
              calculateWorkingHoursForEingruppierung(contract.agEingruppierung2),
            ),
          );
      }
    },
    agInfos: {
      unterzeichnungsdatum: skipInSimpleMode(options => nonOptional()(options)),
      einstellungsdatum: einstellungsdatumValidator(dateRules, simpleValidation),
      gekuendigtZum: gekuendigtZumValidator(dateRules, simpleValidation),
      urlaubsTage: skipInSimpleMode(options => nonOptional()(options)),
    },
    agSozialVersicherung: {
      beitragsgruppenschluesselKV: skipInSimpleMode(options => {
        if (isNullish(options.value)) return nonOptional({ allowFalsy: true })(options);

        if (
          options.value === 9 &&
          getPerson()?.mitarbeiterDaten?.anSozialVersicherung?.versicherungsStatus !== GermanInsuranceTypes.VOLUNTARY
        ) {
          throw new ValidationError(
            'Der Beitragsgruppenschlüssel KV darf nur bei freiwillig Versicherten auf 9 gesetzt werden.',
          );
        }
      }),
      beitragsgruppenschluesselRV: beitragsgruppenschluessel,
      beitragsgruppenschluesselAV: beitragsgruppenschluessel,
      beitragsgruppenschluesselPV: beitragsgruppenschluessel,
    },
    agEingruppierung1: eingruppierungValidator('agEingruppierung1'),
    agEingruppierung2: eingruppierungValidator('agEingruppierung2'),
  } as Validator;
};
